import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Skeleton, Table, Text, Tooltip } from "@mantine/core";
import IconAlertCircle from "@tabler/icons-react/dist/esm/icons/IconAlertCircle.mjs";
import IconFlagFilled from "@tabler/icons-react/dist/esm/icons/IconFlagFilled.mjs";
import { Link } from "react-router-dom";
import { AppPagination } from "../../../components/AppPagination/AppPagination";
import { TableNoResults } from "../../../components/TableNoResults/TableNoResults";
import {
  useSelectManualSearchColumns,
  useSelectManualSearchEnabled,
} from "../../../redux/features/manualSearch/manualSearchSlice";
import { useManualSearchTableQuery } from "./useManualSearchTable.logic";
import ActionsBar from "./ActionsBar";
import classes from "./manualSearchTable.module.scss";

const DEFAULT_TAKE = 20;

export default function ManualSearchTable() {
  const [page, setPage] = useState(0);
  const [take, setTake] = useState(DEFAULT_TAKE);

  const enabled = useSelectManualSearchEnabled();
  const columnsFromStore = useSelectManualSearchColumns();

  const columns = useMemo(
    () =>
      columnsFromStore?.filter(Boolean).map((column) => ({
        key: column,
        label: column,
      })) || [],
    [columnsFromStore],
  );

  useEffect(() => {
    setPage(0);
  }, [enabled]);

  const { data, isFetching, isError } = useManualSearchTableQuery({ page, take });

  if (!enabled) return null;

  if (isError) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={18} />}
        mt="md"
        variant="light"
      >
        Something happened while fetching manual search results.
      </Alert>
    );
  }

  const rows = data?.data || [];
  const totalCount = data?.totalCount || 0;

  return (
    <Box>
      <ActionsBar totalCount={totalCount} />
      <div className="scroll-table-container">
        <Table
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
          className={classes["manual-search-table"]}
        >
          <Table.Thead bg="var(--mantine-color-gray-1)">
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th key={column.key}>{column.label}</Table.Th>
              ))}
              <Table.Th
                className={classes["flagged-column"]}
                style={{ textAlign: "center" }}
              >
                Flagged
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          {rows.length === 0 && !isFetching ? (
            <Table.Tbody>
              <TableNoResults colSpan={columns.length + 1} />
            </Table.Tbody>
          ) : (
            <RenderTableRows />
          )}
        </Table>
      </div>

      {totalCount > 0 && (
        <AppPagination
          onPageChange={(newPage) => setPage(newPage - 1)}
          onTakeChange={setTake}
          page={page}
          take={take}
          total={totalCount}
        />
      )}
    </Box>
  );

  function RenderTableRows() {
    if (isFetching) {
      return <TableSkeleton length={take} columnsCount={columns.length + 1} />;
    }

    return (
      <Table.Tbody>
        {rows.map((row, rowIndex) => (
          <Table.Tr key={row.flagId || rowIndex}>
            {columns.map((column) => {
              const cellValue = row[column.key] ?? "";
              return (
                <Table.Td key={column.key}>
                  <Tooltip
                    label={cellValue}
                    disabled={!cellValue}
                    multiline
                    maw={400}
                  >
                    <Text size="sm" truncate="end">
                      {cellValue}
                    </Text>
                  </Tooltip>
                </Table.Td>
              );
            })}
            <Table.Td style={{ textAlign: "center" }}>
              <FlaggedCell flagId={row.flagId} />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    );
  }
}

function TableSkeleton({ length, columnsCount }) {
  return (
    <Table.Tbody>
      {Array.from({ length }).map((_, rowIndex) => (
        <Table.Tr key={rowIndex}>
          {Array.from({ length: columnsCount }).map((__, columnIndex) => (
            <Table.Td key={columnIndex}>
              <Skeleton height={27} radius="sm" />
            </Table.Td>
          ))}
        </Table.Tr>
      ))}
    </Table.Tbody>
  );
}

function FlaggedCell({ flagId }) {
  if (!flagId) {
    return <IconFlagFilled color="red" />;
  }

  return (
    <Button
      component={Link}
      size="xs"
      target="_blank"
      to={`/reports-for-monitoring/flag-details/${flagId}`}
      variant="light"
    >
      Open
    </Button>
  );
}
